// AR Automation Agent UI (Polished)
// Additions: collection priority heatmap, dunning timeline, credit score radial,
// revenue forecast line chart, validation checklist.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, TrendChart, SimpleBarChart, RadialGauge, ConfidenceChip,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    arCollection, arCashApp, arDunning, arInvoiceValidate, arRevenueForecast, arCreditScore,
} from './api';

const TABS = ['Collection Priority', 'Cash Application', 'Dunning Generator', 'Invoice Validator', 'Revenue Forecast', 'Credit Scorer'];

function priorityColor(score) {
    if (score >= 80) return 'error';
    if (score >= 50) return 'warning';
    return 'success';
}

export default function ARAutomationAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [collection, setCollection] = useState(null);
    const [cashApp, setCashApp] = useState(null);
    const [paymentId, setPaymentId] = useState('');
    const [dunning, setDunning] = useState(null);
    const [customerId, setCustomerId] = useState('');
    const [validation, setValidation] = useState(null);
    const [invoiceId, setInvoiceId] = useState('');
    const [forecast, setForecast] = useState(null);
    const [creditScore, setCreditScore] = useState(null);
    const [creditCustId, setCreditCustId] = useState('');

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !collection) { const r = await run(() => arCollection()); if (r?.data?.success) setCollection(r.data.data); }
            else if (tab === 4 && !forecast) { const r = await run(() => arRevenueForecast()); if (r?.data?.success) setForecast(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Collection Priority', value: (collection?.items || []).filter(c => c.priority_score >= 80).length || '—', accent: 'danger' },
        { label: 'Open Collections', value: (collection?.items || []).length || '—', accent: 'warning' },
        { label: 'Revenue Forecast', value: fmtMoney(forecast?.total_forecast), accent: 'success' },
        { label: 'Avg Collection Days', value: forecast?.avg_collection_days ?? '—', accent: 'info' },
    ];

    const renderTab = () => {
        switch (tab) {
            case 0:
                if (!collection) return <LoadingBlock rows={4} />;
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Smart Collection Priority" subtitle="AI-driven prioritization by customer segment × aging" />
                        <DrillTable
                            columns={[
                                { key: 'customer', label: 'Customer' },
                                { key: 'invoice', label: 'Invoice' },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'days_overdue', label: 'Overdue', align: 'right' },
                                { key: 'priority_score', label: 'Priority', render: r => (
                                    <Chip size="small" color={priorityColor(r.priority_score)} label={r.priority_score} />
                                )},
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Contact</Button> },
                            ]}
                            rows={collection.items || []}
                            maxHeight={480}
                        />
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Cash Application Agent" subtitle="Auto-match incoming payments to open invoices" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Payment ID" size="small" value={paymentId} onChange={e => setPaymentId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!paymentId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => arCashApp(paymentId));
                                    if (r?.data?.success) setCashApp(r.data.data);
                                }}>Apply</Button>
                        </Stack>
                        {cashApp && (
                            <>
                                <DrillTable
                                    columns={[
                                        { key: 'invoice', label: 'Invoice' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'confidence', label: 'Confidence', render: r => <ConfidenceChip value={r.confidence / 100} /> },
                                        { key: 'status', label: 'Status' },
                                    ]}
                                    rows={cashApp.matched_invoices || []}
                                />
                                <AIAnalysisBox text={cashApp.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Dunning Sequence Generator" subtitle="Personalized escalation based on customer history" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Customer ID" size="small" value={customerId} onChange={e => setCustomerId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!customerId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => arDunning(customerId));
                                    if (r?.data?.success) setDunning(r.data.data);
                                }}>Generate</Button>
                        </Stack>
                        {dunning && (
                            <>
                                <DrillTable
                                    columns={[
                                        { key: 'day', label: 'Day' },
                                        { key: 'tier', label: 'Tier', render: r => <Chip size="small" label={r.tier} /> },
                                        { key: 'message_preview', label: 'Preview' },
                                        { key: 'tone', label: 'Tone' },
                                    ]}
                                    rows={dunning.sequence || []}
                                />
                                <AIAnalysisBox text={dunning.ai_message} title="AI Drafted Message" />
                            </>
                        )}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Invoice Accuracy Validator" subtitle="Pre-send validation of pricing, tax, terms, line items" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Invoice ID" size="small" value={invoiceId} onChange={e => setInvoiceId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!invoiceId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => arInvoiceValidate(invoiceId));
                                    if (r?.data?.success) setValidation(r.data.data);
                                }}>Validate</Button>
                        </Stack>
                        {validation && (
                            <>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Stack spacing={0.5}>
                                        {(validation.checks || []).map((c, i) => (
                                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                {c.passed
                                                    ? <CheckCircleIcon fontSize="small" color="success" />
                                                    : <CancelIcon fontSize="small" color="error" />}
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.check_name}</Typography>
                                                {c.detail && <Typography variant="caption" color="text.secondary">— {c.detail}</Typography>}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                                <AIAnalysisBox text={validation.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 4:
                return forecast ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Revenue Forecast" subtitle="Predict collection timing from outstanding AR" />
                        <SummaryGrid cards={[
                            { label: 'Total Forecast', value: fmtMoney(forecast.total_forecast), accent: 'primary' },
                            { label: 'Avg Collection Days', value: forecast.avg_collection_days ?? '—', accent: 'info' },
                            { label: 'Collection Probability', value: forecast.collection_probability != null ? `${forecast.collection_probability}%` : '—', accent: 'success' },
                        ]} cols={3} />
                        {(forecast.weeks || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Weekly Collection Forecast</Typography>
                                <TrendChart
                                    data={forecast.weeks}
                                    xKey="week"
                                    series={[
                                        { key: 'open', label: 'Open', color: CHART_COLORS.primary },
                                        { key: 'recurring', label: 'Recurring', color: CHART_COLORS.secondary },
                                        { key: 'overdue', label: 'Overdue', color: CHART_COLORS.danger },
                                    ]}
                                    height={260}
                                    type="area"
                                    stacked
                                    yFormatter={v => fmtMoney(v)}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'week', label: 'Week' },
                                { key: 'open', label: 'Open', align: 'right', render: r => fmtMoney(r.open) },
                                { key: 'recurring', label: 'Recurring', align: 'right', render: r => fmtMoney(r.recurring) },
                                { key: 'overdue', label: 'Overdue', align: 'right', render: r => fmtMoney(r.overdue) },
                                { key: 'total', label: 'Total', align: 'right', render: r => <b>{fmtMoney(r.total)}</b> },
                            ]}
                            rows={forecast.weeks || []}
                        />
                        <AIAnalysisBox text={forecast.ai_analysis} />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 5:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Customer Credit Scorer" subtitle="Continuous credit risk assessment" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Customer ID (optional)" size="small" value={creditCustId} onChange={e => setCreditCustId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => arCreditScore(creditCustId || undefined));
                                    if (r?.data?.success) setCreditScore(r.data.data);
                                }}>Score</Button>
                        </Stack>
                        {creditScore && (
                            <>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={4}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600}>Credit Score</Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <RadialGauge
                                                    value={creditScore.score || 0}
                                                    label={creditScore.score >= 80 ? 'Strong' : creditScore.score >= 60 ? 'OK' : 'Weak'}
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Grid container spacing={1}>
                                            {[
                                                { label: 'Payment History', key: 'payment_history' },
                                                { label: 'Dispute Rate', key: 'dispute_rate' },
                                                { label: 'Overdue Ratio', key: 'overdue_ratio' },
                                                { label: 'Order Frequency', key: 'order_frequency' },
                                            ].map(comp => (
                                                <Grid item xs={6} key={comp.key}>
                                                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">{comp.label}</Typography>
                                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                            {creditScore.components?.[comp.key] != null ? creditScore.components[comp.key] : '—'}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <AIAnalysisBox text={creditScore.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="AR Automation Agent"
            subtitle="Agentic Autonomous Workflow — collection priority, cash application, dunning, invoice validation, revenue forecast, credit scoring."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
